import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import {
    fetchMeAdmin,
    fetchOrgAdmins,
    fetchOrganizations,
    fetchOrgTenants,
    OrgAdminUnauthorizedError,
    type Organization,
} from '@/api/orgAdmin';
import { Button, EmptyState, ErrorState, Input, PageHeader, Select, SkeletonTable } from '@/components/ui';
import { OrganizationsTable, type OrgRowStats } from './Organizations/OrganizationsTable';
import { OrganizationPeekDrawer } from './Organizations/OrganizationPeekDrawer';
import { CreateOrganizationModal } from './Organizations/CreateOrganizationModal';
import { MoveOrganizationModal } from '@/components/organizations/MoveOrganizationModal';
import styles from './OrganizationsPage.module.css';

const SEARCH_DEBOUNCE_MS = 300;
/** Rows per CLIENT-side page of the filtered list. */
const PAGE_SIZE = 20;
/**
 * EAA-1815: rows per REQUEST of the root-org walk below, which fetches every
 * page and merges them. It is deliberately larger than `PAGE_SIZE`: the walk's
 * request count is `ceil(roots / this)`, and the server-side page size has
 * nothing to do with how many rows the table shows at a time. At the old
 * default of 20 a 200-org registry cost ten round trips to display twenty rows.
 */
const FETCH_PAGE_SIZE = 100;

// EAA-1818: `no_tenant` renamed to `no_unit` — the tab now measures child-org
// ("đơn vị") count, not tenant count (see `orgMatchesTab` below). This union
// member's literal values double as the `?status=` URL value (see
// `rawStatus` parsing just below), so the rename intentionally touches both
// sides together — there is no separate mapping layer that could drift.
type StatusTab = 'all' | 'active' | 'inactive' | 'no_unit';

/**
 * EAA-1815: `/organizations` — rebuilt per handoff frame "21 Organizations".
 *
 * Every page of `GET /organizations?parent_id=null` (search/ordering held fixed)
 * is fetched and merged — ROOT orgs only, the same "load it all, filter
 * client-side" shape `AppsPage` already uses for its registry, since
 * `/organizations` is the REGISTRY of root orgs, not a flat dump of every đơn
 * vị at every depth (EAA-1818 fix — see the `page1Query`/`extraPagesQueries`
 * comment below). Rows carry `child_count` and `account_count`, so the "Đơn vị"
 * column, the row health pill, the "Chưa có đơn vị" tab and the "Tài khoản"
 * column all read the SAME response — no per-row fetch.
 *
 * EAA-1815 (2026-09-09, user report: the console "call too max API at once").
 * That used to be three `useQueries` fan-outs, one entry per org, and the page
 * opened ~74 XHRs for 26 orgs:
 *   - `?parent_id={id}&page_size=1` per root, read for `.count` alone;
 *   - `/{id}/assignments?page_size=1` per visible row, likewise;
 *   - `/{id}/tenants` per root — the worst of the three, because it fetched a
 *     FULL tenant list for every org so that the quick-peek drawer could show
 *     ONE of them. 25 of 26 responses were discarded.
 * portal-api now annotates the first two onto the list row
 * (`OrganizationListSerializer` — services/portal-api/portal_api/directory/
 * org_serializers.py), and the tenant list is fetched for the peeked org only.
 * Do not reintroduce a per-row count query: if a new column needs an aggregate,
 * annotate it on the list endpoint the same way.
 *
 * No number here is invented — anything not yet resolved renders "…", never
 * a guess. A row whose `child_count`/`account_count` is absent (an older
 * portal-api, or a row that did not come from the list endpoint) renders that
 * same "…" rather than a fabricated 0.
 */
export function OrganizationsPage() {
    const { t } = useTranslation();
    const can = useCan();
    const config = useRuntimeConfig();
    const { authenticated, accessToken, reauthenticate } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const orderingOptions = useMemo(
        () => [
            { value: 'name', label: t('admin.organizations.orderingOptions.nameAsc') },
            { value: '-name', label: t('admin.organizations.orderingOptions.nameDesc') },
            { value: 'code', label: t('admin.organizations.orderingOptions.codeAsc') },
            { value: '-code', label: t('admin.organizations.orderingOptions.codeDesc') },
            { value: '-created_at', label: t('admin.organizations.orderingOptions.newestFirst') },
            { value: 'created_at', label: t('admin.organizations.orderingOptions.oldestFirst') },
        ],
        [t]
    );
    const statusTabs = useMemo(
        (): { key: StatusTab; label: string }[] => [
            { key: 'all', label: t('admin.organizations.statusTabs.all') },
            { key: 'active', label: t('admin.organizations.status.active') },
            { key: 'inactive', label: t('admin.organizations.statusTabs.inactive') },
            { key: 'no_unit', label: t('admin.organizations.status.noUnit') },
        ],
        [t]
    );

    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [ordering, setOrdering] = useState('name');
    const [page, setPage] = useState(1);
    const [peekOrgId, setPeekOrgId] = useState<string | null>(null);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    // EAA-1891: the org whose cross-tree move dialog is open. Holds the ROW, not
    // just an id, so the dialog can show its name and current parent without a
    // second lookup — the list already has the full record.
    const [moveOrg, setMoveOrg] = useState<Organization | null>(null);

    const rawStatus = searchParams.get('status');
    const statusTab: StatusTab =
        rawStatus === 'active' || rawStatus === 'inactive' || rawStatus === 'no_unit'
            ? rawStatus
            : 'all';
    const setStatusTab = useCallback(
        (next: StatusTab) => {
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                if (next === 'all') params.delete('status');
                else params.set('status', next);
                return params;
            });
        },
        [setSearchParams]
    );

    useEffect(() => {
        const handle = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [searchInput]);

    useEffect(() => {
        setPage(1);
    }, [search, ordering, statusTab]);

    const enabled = authenticated && Boolean(accessToken) && can('console.orgs.view');

    const meAdminQuery = useQuery({
        queryKey: ['me-admin', accessToken],
        queryFn: () => fetchMeAdmin(config.apiBase, accessToken as string),
        enabled,
        retry: false,
    });
    const isSuperAdmin = meAdminQuery.data?.is_super_admin ?? false;

    // EAA-1818: `/organizations` is the org REGISTRY — it must list only ROOT
    // orgs, never every đơn vị at every depth. `fetchOrganizations`'s
    // `parentId` param is deliberately three-state (see `src/api/orgAdmin.ts`):
    // `undefined` omits `parent_id` (no filter, every depth — the bug this
    // fixes), `null` requests roots only, a uuid requests that org's direct
    // children. Passing `null` here is the fix; do not simplify it back to
    // `undefined`. `parentId` also has to sit in the queryKey (both here and
    // on the extra-pages fan-out below) — otherwise a flat result cached
    // under the same key from before this filter existed would be served
    // stale. Note: with the root filter on, `search` no longer matches child
    // đơn vị — that is intended, đơn vị search lives on `/console/hierarchy`.
    //
    // Page 1 tells us `total_pages`; the rest fan out in parallel below.
    const page1Query = useQuery({
        queryKey: ['organizations-list', accessToken, search, ordering, null, FETCH_PAGE_SIZE, 1],
        queryFn: () =>
            fetchOrganizations(config.apiBase, accessToken as string, {
                page: 1,
                pageSize: FETCH_PAGE_SIZE,
                search,
                ordering,
                parentId: null,
            }),
        enabled,
        retry: false,
    });

    // EAA-1825: loop-guarded — see AuthProvider.reauthenticate / signInLoop.ts.
    if (page1Query.error instanceof OrgAdminUnauthorizedError) {
        reauthenticate('/organizations');
    }

    const totalPages = page1Query.data?.total_pages ?? 1;
    const extraPages = useMemo(
        () => Array.from({ length: Math.max(0, totalPages - 1) }, (_, i) => i + 2),
        [totalPages]
    );
    const extraPagesQueries = useQueries({
        queries: extraPages.map((p) => ({
            queryKey: ['organizations-list', accessToken, search, ordering, null, FETCH_PAGE_SIZE, p],
            queryFn: () =>
                fetchOrganizations(config.apiBase, accessToken as string, {
                    page: p,
                    pageSize: FETCH_PAGE_SIZE,
                    search,
                    ordering,
                    parentId: null,
                }),
            enabled: enabled && page1Query.isSuccess,
            retry: false,
        })),
    });

    const allOrgs = useMemo(() => {
        if (!page1Query.data) return [] as Organization[];
        const merged = [...page1Query.data.results];
        for (const q of extraPagesQueries) {
            if (q.data) merged.push(...q.data.results);
        }
        return merged;
         
    }, [page1Query.data, extraPagesQueries]);

    const allPagesLoaded = page1Query.isSuccess && extraPagesQueries.every((q) => q.isSuccess);

    // EAA-1818: real CHILD-ORG counts for every loaded root — powers the
    // "Đơn vị" column, the row health pill, and the "Chưa có đơn vị" tab.
    // `Organization.parent` (the đơn vị tree) is a different backend entity
    // from `OrganizationAppScope` (app scope, which does not nest); the two
    // must not be conflated.
    //
    // EAA-1815: read straight off the list row. This was one
    // `?parent_id={id}&page_size=1` request PER ORG whose response was thrown
    // away except for `.count` — 26 requests to fill one column. portal-api
    // annotates the same number as `child_count` now, so the count arrives with
    // the row it belongs to. A row from an older portal-api has no field and
    // stays out of the map, which is the "…" state the table already renders.
    const unitCountById = useMemo(() => {
        const map: Record<string, number> = {};
        for (const org of allOrgs) {
            if (typeof org.child_count === 'number') map[org.id] = org.child_count;
        }
        return map;
    }, [allOrgs]);
    // The counts land with the rows, so there is nothing extra to wait for.
    const unitCountsLoaded = allPagesLoaded;

    const matchesTab = useCallback(
        (org: Organization) => orgMatchesTab(org, statusTab, unitCountById),
        [statusTab, unitCountById]
    );

    const visibleOrgs = useMemo(() => allOrgs.filter(matchesTab), [allOrgs, matchesTab]);
    const totalVisiblePages = Math.max(1, Math.ceil(visibleOrgs.length / PAGE_SIZE));
    const pageOrgs = useMemo(
        () => visibleOrgs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [visibleOrgs, page]
    );

    // EAA-1815: account totals ride on the row as `account_count`. This was one
    // `/{id}/assignments?page_size=1` per RENDERED row (20 more requests) read
    // for `.count` alone — see the header comment.
    const rowStats = useMemo(() => {
        const map: Record<string, OrgRowStats> = {};
        for (const org of pageOrgs) {
            map[org.id] = {
                unitCount: unitCountById[org.id],
                accountCount: org.account_count,
            };
        }
        return map;
    }, [pageOrgs, unitCountById]);

    const tabCount = useCallback(
        (key: StatusTab): number | undefined => {
            if (key === 'all') return allPagesLoaded ? allOrgs.length : undefined;
            if (!allPagesLoaded || !unitCountsLoaded) return undefined;
            return allOrgs.filter((org) => orgMatchesTab(org, key, unitCountById)).length;
        },
        [allOrgs, allPagesLoaded, unitCountsLoaded, unitCountById]
    );

    // --- quick peek -------------------------------------------------------
    const peekOrg = allOrgs.find((org) => org.id === peekOrgId) ?? null;
    /**
     * EAA-1815: the drawer's tenant listing — real `OrgTenant` rows, not a
     * count, so there is nothing on the list row to derive it from.
     *
     * Fetched for the PEEKED org only. It used to be a `useQueries` fan-out over
     * every loaded org, firing one full `/{id}/tenants` request per row on page
     * load so that a drawer the user might never open would already be warm.
     * The key is the same `['org-tenants', orgId, token]` the hierarchy screen
     * uses, so a peek and a hierarchy visit still share one cached response.
     */
    const peekTenantsQuery = useQuery({
        queryKey: ['org-tenants', peekOrgId, accessToken],
        queryFn: () => fetchOrgTenants(config.apiBase, accessToken as string, peekOrgId as string),
        enabled: enabled && Boolean(peekOrgId),
        retry: false,
    });
    const peekAdminsQuery = useQuery({
        queryKey: ['organization-admins', peekOrgId, accessToken],
        queryFn: () => fetchOrgAdmins(config.apiBase, accessToken as string, peekOrgId as string),
        enabled: enabled && Boolean(peekOrgId),
        retry: false,
    });

    const openDetail = (org: Organization) => navigate(`/organizations/${org.id}`);
    const navigateTab = (org: Organization, tab: 'tenants' | 'admins' | 'branding') =>
        navigate(`/organizations/${org.id}?tab=${tab}`);
    const createTenantFor = (org: Organization) =>
        navigate(`/organizations/${org.id}?tab=tenants&action=create`);

    const isLoading = page1Query.isLoading;
    const isError = page1Query.isError && !(page1Query.error instanceof OrgAdminUnauthorizedError);

    if (!can('console.orgs.view')) {
        return null;
    }

    return (
        <>
            <main className="portal-admin portal-console-page portal-console-page--fit">
                <PageHeader
                    breadcrumb={
                        <span className={styles.topline}>
                            <span>{t('admin.organizations.orgWord')}</span>
                            <span>/</span>
                            <span style={{ color: 'var(--color-text)', fontWeight: 700 }}>
                                {t('admin.organizations.listWord')}
                            </span>
                        </span>
                    }
                    title={t('admin.organizations.title')}
                    subtitle={
                        <span className={styles.subtitle}>
                            {/* EAA-1818: "kế thừa" (inherit) was wrong — app scope is per-đơn-vị
                                and explicitly does NOT inherit down the tree. Replaced with the
                                design's wording, which states the tree/ownership fact instead. */}
                            {t('admin.organizations.subtitle')}
                        </span>
                    }
                    actions={
                        isSuperAdmin && (
                            // EAA-1804: kit `Button` instead of hand-written
                            // `<button className="portal-btn portal-btn-primary">`. The icon
                            // child is left as-is (rule: prefer leaving children untouched).
                            <Button type="primary" onClick={() => setCreateModalOpen(true)}>
                                <svg
                                    viewBox="0 0 24 24"
                                    width="16"
                                    height="16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    aria-hidden="true"
                                >
                                    <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                                </svg>
                                {t('admin.organizations.createOrgAction')}
                            </Button>
                        )
                    }
                />

                <nav
                    className={styles.tabs}
                    aria-label={t('admin.organizations.statusFilterAriaLabel')}
                >
                    {statusTabs.map((tab) => {
                        const count = tabCount(tab.key);
                        const active = tab.key === statusTab;
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                className={styles.tab + (active ? ' is-active' : '')}
                                onClick={() => setStatusTab(tab.key)}
                            >
                                {tab.label}
                                {count !== undefined && (
                                    <span className={styles.tabCount}>{count}</span>
                                )}
                            </button>
                        );
                    })}
                </nav>

                <div className={styles.toolbar}>
                    {/* EAA-1804: raw `<input>` with a hand-drawn magnifier `<label>` → kit
                        `Input` with the icon in its `prefix` slot (mirrors `AppsToolbar`'s
                        search box). */}
                    <Input
                        className="portal-orgs-search"
                        type="search"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder={t('admin.organizations.searchPlaceholder')}
                        aria-label={t('common.search')}
                        allowClear
                        prefix={
                            <svg
                                viewBox="0 0 24 24"
                                width="16"
                                height="16"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                aria-hidden="true"
                            >
                                <circle cx="11" cy="11" r="7" />
                                <path d="m20 20-3.5-3.5" strokeLinecap="round" />
                            </svg>
                        }
                    />
                    {/* EAA-1804: raw `<select>` + hand-drawn chevron `<svg>` → kit `Select` —
                        the chevron comes free from antd, so the sibling svg is dropped. */}
                    <Select
                        className="portal-orgs-sort"
                        value={ordering}
                        onChange={(value: string) => setOrdering(value)}
                        options={orderingOptions.map((option) => ({
                            value: option.value,
                            label: t('admin.organizations.sortOptionLabel', { label: option.label }),
                        }))}
                    />
                    <span className={styles.toolbarSpacer} />
                    {allPagesLoaded && (
                        <span className={styles.countNote}>
                            {t('admin.organizations.countNote', {
                                shown: pageOrgs.length,
                                total: visibleOrgs.length,
                            })}
                        </span>
                    )}
                </div>

                {isLoading && (
                    <div role="status" aria-live="polite">
                        <span className="portal-sr-only">
                            {t('admin.organizations.loadingList')}
                        </span>
                        <SkeletonTable rows={5} columns={6} />
                    </div>
                )}
                {isError && (
                    <ErrorState
                        description={t('admin.organizations.loadErrorDescription')}
                        onRetry={() => void page1Query.refetch()}
                        retrying={page1Query.isFetching}
                    />
                )}

                {page1Query.isSuccess &&
                    pageOrgs.length === 0 &&
                    (search || statusTab !== 'all' ? (
                        <EmptyState
                            title={t('admin.organizations.emptyFilteredTitle')}
                            description={t('admin.organizations.emptyFilteredDescription')}
                        />
                    ) : (
                        <EmptyState
                            title={t('admin.organizations.emptyNoneTitle')}
                            action={
                                isSuperAdmin && (
                                    <Button type="primary" onClick={() => setCreateModalOpen(true)}>
                                        {t('admin.organizations.createOrgAction')}
                                    </Button>
                                )
                            }
                        />
                    ))}

                {/* EAA-1804: the table is this page's fit-grow region. Before this the page
                    had no `--fit`, so the org list grew unbounded and the whole page scrolled
                    on the shell — taking the sticky table header with it. The table's own root
                    carries `.fit-scroll` (see 22-fit-to-shell.css) so it becomes the one
                    scroll region without the shared chain flexing the `<table>` itself. */}
                {pageOrgs.length > 0 && (
                    <div className={`${styles.tableRegion} fit-grow`}>
                        <OrganizationsTable
                            organizations={pageOrgs}
                            stats={rowStats}
                            selectedOrgId={peekOrgId}
                            onPeek={(org) => setPeekOrgId(org.id)}
                            onOpenDetail={openDetail}
                            onMoveIntoOrg={isSuperAdmin ? setMoveOrg : undefined}
                        />
                    </div>
                )}

                {visibleOrgs.length > 0 && (
                    <div className="portal-console-pagination">
                        <span>
                            {t('admin.organizations.paginationLabel', {
                                page,
                                totalPages: totalVisiblePages,
                                count: visibleOrgs.length,
                            })}
                        </span>
                        <span style={{ flex: 1 }} />
                        {/* EAA-1804: these were bare `<button>`s that inherited the portal-btn
                            skin only via the legacy `.portal-console-pagination button:not(.ant-btn)`
                            selector (01-controls.css) — no `portal-btn` class was on the element
                            itself. Swapping to kit `Button` keeps the same look, sourced from the
                            antd theme instead of that selector. */}
                        <Button disabled={page <= 1} onClick={() => setPage(page - 1)}>
                            {t('admin.organizations.paginationPrev')}
                        </Button>
                        <Button
                            disabled={page >= totalVisiblePages}
                            onClick={() => setPage(page + 1)}
                        >
                            {t('admin.organizations.paginationNext')}
                        </Button>
                    </div>
                )}
            </main>

            <OrganizationPeekDrawer
                org={peekOrg}
                tenants={peekTenantsQuery.data}
                tenantsLoading={peekTenantsQuery.isLoading}
                admins={peekAdminsQuery.data}
                adminsLoading={peekAdminsQuery.isLoading}
                accountCount={peekOrgId ? rowStats[peekOrgId]?.accountCount : undefined}
                onClose={() => setPeekOrgId(null)}
                onOpenDetail={openDetail}
                onNavigateTab={navigateTab}
                onCreateTenant={createTenantFor}
            />

            {isSuperAdmin && (
                <CreateOrganizationModal
                    open={createModalOpen}
                    onClose={() => setCreateModalOpen(false)}
                />
            )}

            {/* EAA-1891: cross-tree adoption. Rendered only for a super-admin,
                matching the row action's own gate above; the modal itself is
                shared with /console/hierarchy so the two surfaces cannot drift. */}
            {isSuperAdmin && (
                <MoveOrganizationModal
                    open={moveOrg !== null}
                    organization={moveOrg}
                    onClose={() => setMoveOrg(null)}
                />
            )}
        </>
    );
}

// EAA-1818: takes the child-org ("đơn vị") count map, not a tenant count map —
// renamed together with the `no_tenant` -> `no_unit` `StatusTab` member above,
// since this is the function that interprets that key.
function orgMatchesTab(
    org: Organization,
    key: StatusTab,
    unitCountById: Record<string, number>
): boolean {
    if (key === 'active') return org.is_active && unitCountById[org.id] !== 0;
    if (key === 'inactive') return !org.is_active;
    if (key === 'no_unit') return org.is_active && unitCountById[org.id] === 0;
    return true;
}
